import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, Grid, Chip, CircularProgress } from '@mui/material';
import Button from '@mui/material/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo } from '@fortawesome/free-solid-svg-icons';
import axios, { decryptData } from '../Axiosinstance';
import { baseURLdyte, Password, Username, dyteRestHeader } from '../common';

const ActiveSessionsCard = () => {
  const [liveSessions, setLiveSessions] = useState([]);
  const [batchMap, setBatchMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [joiningId, setJoiningId] = useState(null);

  // ── 1. Build meetingId → course name map from batches ─────────────────────
  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const res = await axios.get('batches/search', {
          params: { size: 200, page: 0 }
        });
        const map = {};
        res.data.content.forEach(batch => {
          const match = batch.onlineclasslink?.match(/id=([a-z0-9-]+)/i);
          if (match) {
            map[match[1]] = {
              course: batch.course,
              batchId: batch.id,
              meetingId: match[1],
            };
          }
        });
        setBatchMap(map);
      } catch (err) {
        console.error('Error fetching batches:', err);
      }
    };
    fetchBatches();
  }, []);

  // ── 2. Fetch live Dyte sessions ────────────────────────────────────────────
  const fetchLiveSessions = async () => {
    try {
      const authHeader = `Basic ${btoa(`${Username}:${Password}`)}`;
      const res = await axios.get(`${baseURLdyte}/sessions`, {
        headers: { Authorization: authHeader },
      });

      const allSessions = res.data.data.sessions;

      // Live = started but not ended, and has participants
      // const live = allSessions.filter(
      //   s => s.started_at && !s.ended_at && s.total_participants > 0
      // );

          const live = allSessions.filter(s => {
      const meetingId = s.meta?.room_name;
      return (
        s.started_at &&
        !s.ended_at &&
        s.total_participants > 0 &&
        batchMap[meetingId]   // ✅ only show sessions belonging to THIS project
      );
    });

      setLiveSessions(live);
    } catch (err) {
      console.error('Error fetching live sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   fetchLiveSessions();
  //   // Poll every 30 seconds
  //   const interval = setInterval(fetchLiveSessions, 30000);
  //   return () => clearInterval(interval);
  // }, []);
  useEffect(() => {
  if (Object.keys(batchMap).length === 0) return; // wait for batchMap

  fetchLiveSessions();
  const interval = setInterval(fetchLiveSessions, 30000);
  return () => clearInterval(interval);
}, [batchMap]); // ← depend on batchMap

  // ── 3. Join meeting ────────────────────────────────────────────────────────
  const handleJoin = async (meetingId) => {
    setJoiningId(meetingId);
    try {
      const loggedInUserId = decryptData(localStorage.getItem('userId'));
      const userDetailsResponse = await axios.get(`home/user/${loggedInUserId}`);
      const userDetails = userDetailsResponse.data;

      const response = await axios.post(
        `${baseURLdyte}/meetings/${meetingId}/participants`,
        {
          name: (userDetails.name || 'Admin') + ' Admin',
          picture:
            userDetails.profile_Url ||
            'https://img.freepik.com/premium-vector/man-avatar-profile-picture-vector-illustration_268834-538.jpg',
          preset_name: 'group_call_host',
          custom_participant_id: String(userDetails.id),
        },
        {
          headers: {
            Authorization: dyteRestHeader,
            'Content-Type': 'application/json',
          },
        }
      );

      const token = response.data?.data?.token;
      if (!token) {
        alert('❌ Token not received');
        return;
      }
      window.open(`https://meet.dyte.io/${meetingId}?authToken=${token}`, '_blank');
    } catch (err) {
      console.error('Error joining meeting:', err);
      alert('Failed to join meeting.');
    } finally {
      setJoiningId(null);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const formatStartTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getElapsedMinutes = (startedAt) => {
    const diff = Date.now() - new Date(startedAt).getTime();
    return Math.floor(diff / 60000);
  };

  return (
    <Card
      sx={{
        borderRadius: '15px',
        background: 'linear-gradient(135deg, #ffffff, #e0e0e0)',
        boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.1)',
        minHeight: '160px',
      }}
    >
      <CardContent>
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              backgroundColor: '#e8f5e9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FontAwesomeIcon icon={faVideo} style={{ color: '#2e7d32', fontSize: '18px' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Active Online Sessions
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Live sessions happening right now
            </Typography>
          </Box>
          {/* Live pulse badge */}
          {liveSessions.length > 0 && (
            <Chip
              label={`${liveSessions.length} LIVE`}
              size="small"
              sx={{
                ml: 'auto',
                backgroundColor: '#ff1744',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '11px',
                animation: 'pulse 1.5s infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                  '100%': { opacity: 1 },
                },
              }}
            />
          )}
        </Box>

        {/* ── Body ────────────────────────────────────────────────────────── */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>

        ) : liveSessions.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 3,
              backgroundColor: '#f9f9f9',
              borderRadius: '10px',
            }}
          >
            <Typography variant="body2" color="textSecondary">
              No active sessions right now
            </Typography>
            <Typography variant="caption" color="textSecondary">
              This updates every 30 seconds
            </Typography>
          </Box>

        ) : (
          <Grid container spacing={2}>
            {liveSessions.map((session) => {
              const meetingId = session.meta?.room_name;
              const batchInfo = batchMap[meetingId];
              const elapsed = getElapsedMinutes(session.started_at);

              return (
                <Grid item xs={12} sm={6} key={session.id}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: '10px',
                      backgroundColor: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    {/* Course name */}
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: '#166534' }}
                    >
                      {batchInfo?.course || session.meeting_display_name || 'Online Session'}
                    </Typography>

                    {/* Meta info */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={`👥 ${session.total_participants} participant${session.total_participants !== 1 ? 's' : ''}`}
                        size="small"
                        sx={{ backgroundColor: '#dcfce7', fontSize: '11px' }}
                      />
                      <Chip
                        label={`⏱ ${elapsed} min`}
                        size="small"
                        sx={{ backgroundColor: '#fef9c3', fontSize: '11px' }}
                      />
                      <Chip
                        label={`🕐 Started ${formatStartTime(session.started_at)}`}
                        size="small"
                        sx={{ backgroundColor: '#e0f2fe', fontSize: '11px' }}
                      />
                    </Box>

                    {/* Join button */}
                    <Button
                      variant="contained"
                      size="small"
                      disabled={joiningId === meetingId}
                      onClick={() => handleJoin(meetingId)}
                      startIcon={
                        joiningId === meetingId ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <FontAwesomeIcon icon={faVideo} />
                        )
                      }
                      sx={{
                        mt: 0.5,
                        backgroundColor: '#16a34a',
                        '&:hover': { backgroundColor: '#15803d' },
                        textTransform: 'none',
                        fontWeight: 600,
                        alignSelf: 'flex-start',
                      }}
                    >
                      {joiningId === meetingId ? 'Joining...' : 'Join Meeting'}
                    </Button>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default ActiveSessionsCard;